import AddBoxSharpIcon from '@mui/icons-material/AddBoxSharp';
import DisabledByDefaultSharpIcon from '@mui/icons-material/DisabledByDefaultSharp';
import { ButtonBase, Stack, Typography } from '@mui/material';
import {
  QuestionnaireLanguages,
  i18nClosedSubCategory,
  i18nField,
} from 'features/lead/lead-management/lead-questionnaire-details/lead-questionnaire-details.types';
import PaiTextField from 'features/ui/base-components/pai-text-field';
import { BasicActionDialog } from 'features/ui/base-dialogs';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

interface Props {
  open: boolean;
  categoryName: i18nField;
  categoryInstructionSelf: i18nField;
  categoryInstructionExternal: i18nField;
  subCategories: i18nClosedSubCategory[];

  handleClose(): void;

  handleProceed(): void;

  handleCategoryNameChange(lang: QuestionnaireLanguages): (event: React.ChangeEvent<HTMLInputElement>) => void;

  handleCategoryInstructionSelfChange(lang: QuestionnaireLanguages): (event: React.ChangeEvent<HTMLInputElement>) => void;

  handleCategoryInstructionExternalChange(lang: QuestionnaireLanguages): (event: React.ChangeEvent<HTMLInputElement>) => void;

  addNewSubCategory(): void;

  addNewQuestionToClosedQuestionSubCategory(subCategoryIndex: number): () => void;

  removeQuestionFromClosedQuestionSubCategory(subCategoryIndex: number, questionIndex: number): () => void;

  removeSubCategory(subCategoryIndex: number): () => void;

  handleSubCategoryNameChange(subCategoryIndex: number, lang: QuestionnaireLanguages): (event: React.ChangeEvent<HTMLInputElement>) => void;

  handleSubCategoryDescriptionChange(subCategoryIndex: number, lang: QuestionnaireLanguages): (event: React.ChangeEvent<HTMLInputElement>) => void;

  handleSelfAssessmentQuestionChange(
    subCategoryIndex: number,
    questionIndex: number,
    lang: QuestionnaireLanguages
  ): (event: React.ChangeEvent<HTMLInputElement>) => void;

  handleExternalAssessmentQuestionChange(
    subCategoryIndex: number,
    questionIndex: number,
    lang: QuestionnaireLanguages
  ): (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const AddNewClosedQuestionDialog: React.FC<Props> = ({
  open,
  categoryName,
  categoryInstructionSelf,
  categoryInstructionExternal,
  subCategories,
  handleClose,
  handleProceed,
  handleCategoryNameChange,
  handleCategoryInstructionSelfChange,
  handleCategoryInstructionExternalChange,
  addNewSubCategory,
  addNewQuestionToClosedQuestionSubCategory,
  removeQuestionFromClosedQuestionSubCategory,
  removeSubCategory,
  handleSubCategoryDescriptionChange,
  handleSubCategoryNameChange,
  handleExternalAssessmentQuestionChange,
  handleSelfAssessmentQuestionChange,
}) => {
  const { classes } = useStyles();

  const isSubCategoryEmpty = () => {
    if (
      subCategories.some((category) => category.name.de.trim() === '' || category.name.en.trim() === '') ||
      subCategories.some((category) => category.description.de.trim() === '' || category.description.en.trim() === '') ||
      subCategories.some(
        (category) =>
          category.questions.some((question) => question.selfAssessment.de.trim() === '') ||
          category.questions.some((question) => question.selfAssessment.en.trim() === '') ||
          category.questions.some((question) => question.externalAssessment.de.trim() === '') ||
          category.questions.some((question) => question.externalAssessment.en.trim() === '')
      )
    ) {
      return true;
    }

    return false;
  };

  return (
    <BasicActionDialog
      open={open}
      maxWidth={'md'}
      dialogTitle={'Kategorie hinzufügen'}
      proceedButtonText={'Hinzufügen'}
      cancelButtonText={'Abbrechen'}
      handleClose={handleClose}
      handleProceed={handleProceed}
      proceedButtonDisabled={
        isSubCategoryEmpty() ||
        categoryName.de.trim() === '' ||
        categoryName.en.trim() === '' ||
        categoryInstructionSelf.de.trim() === '' ||
        categoryInstructionSelf.en.trim() === '' ||
        categoryInstructionExternal.de.trim() === '' ||
        categoryInstructionExternal.en.trim() === ''
      }
    >
      <Stack gap={2}>
        <Stack className={classes.container} gap={2}>
          <Stack direction={'row'} alignContent={'center'} gap={2}>
            <PaiTextField
              value={categoryName.de}
              label={'Kategorie (deutsch)'}
              placeholder={'Name der Kategorie eingeben...'}
              onChange={handleCategoryNameChange(QuestionnaireLanguages.DE)}
            />
            <PaiTextField
              value={categoryName.en}
              label={'Kategorie (englisch)'}
              placeholder={'Name der Kategorie eingeben...'}
              onChange={handleCategoryNameChange(QuestionnaireLanguages.EN)}
            />
          </Stack>
          <Stack direction={'row'} alignContent={'center'} gap={2}>
            <PaiTextField
              value={categoryInstructionSelf.de}
              label={'Beschreibung Perspektive Selbsteinschätzung (deutsch)'}
              placeholder={'Beschreibung der Kategorie eingeben...'}
              onChange={handleCategoryInstructionSelfChange(QuestionnaireLanguages.DE)}
            />
            <PaiTextField
              value={categoryInstructionSelf.en}
              label={'Beschreibung Perspektive Selbsteinschätzung (englisch)'}
              placeholder={'Beschreibung der Kategorie eingeben...'}
              onChange={handleCategoryInstructionSelfChange(QuestionnaireLanguages.EN)}
            />
          </Stack>
          <Stack direction={'row'} alignContent={'center'} gap={2}>
            <PaiTextField
              value={categoryInstructionExternal.de}
              label={'Beschreibung Perspektive Fremdeinschätzung (deutsch)'}
              placeholder={'Beschreibung der Kategorie eingeben...'}
              onChange={handleCategoryInstructionExternalChange(QuestionnaireLanguages.DE)}
            />
            <PaiTextField
              value={categoryInstructionExternal.en}
              label={'Beschreibung Perspektive Fremdeinschätzung (englisch)'}
              placeholder={'Beschreibung der Kategorie eingeben...'}
              onChange={handleCategoryInstructionExternalChange(QuestionnaireLanguages.EN)}
            />
          </Stack>
        </Stack>
        {subCategories.map((subCategory, subCategoryIdx) => (
          <Stack key={subCategoryIdx} className={classes.container} gap={2}>
            <Stack className={classes.container} gap={2}>
              <Stack direction={'row'} alignContent={'center'} gap={2}>
                <PaiTextField
                  value={subCategory.name.de}
                  label={'Unterkategorie (deutsch)'}
                  placeholder={'Name der Unterkategorie eingeben...'}
                  onChange={handleSubCategoryNameChange(subCategoryIdx, QuestionnaireLanguages.DE)}
                />
                <PaiTextField
                  value={subCategory.name.en}
                  label={'Unterkategorie (englisch)'}
                  placeholder={'Name der Unterkategorie eingeben...'}
                  onChange={handleSubCategoryNameChange(subCategoryIdx, QuestionnaireLanguages.EN)}
                />
              </Stack>
              <Stack direction={'row'} alignContent={'center'} gap={2}>
                <PaiTextField
                  value={subCategory.description.de}
                  label={'Beschreibung (deutsch)'}
                  placeholder={'Beschreibung der Unterkategorie eingeben...'}
                  onChange={handleSubCategoryDescriptionChange(subCategoryIdx, QuestionnaireLanguages.DE)}
                />
                <PaiTextField
                  value={subCategory.description.en}
                  label={'Beschreibung (englisch)'}
                  placeholder={'Beschreibung der Unterkategorie eingeben...'}
                  onChange={handleSubCategoryDescriptionChange(subCategoryIdx, QuestionnaireLanguages.EN)}
                />
              </Stack>
            </Stack>
            {subCategory.questions.map((question, questionIdx) => (
              <Stack key={questionIdx} className={classes.container} gap={2}>
                <Stack direction={'row'} alignContent={'center'} gap={2}>
                  <PaiTextField
                    value={question.selfAssessment.de}
                    placeholder={'Fragetext zur Selbstbewertung...'}
                    label={'Selbstbewertung (deutsch)'}
                    onChange={handleSelfAssessmentQuestionChange(subCategoryIdx, questionIdx, QuestionnaireLanguages.DE)}
                  />
                  <PaiTextField
                    value={question.selfAssessment.en}
                    placeholder={'Fragetext zur Selbstbewertung...'}
                    label={'Selbstbewertung (englisch)'}
                    onChange={handleSelfAssessmentQuestionChange(subCategoryIdx, questionIdx, QuestionnaireLanguages.EN)}
                  />
                </Stack>
                <Stack direction={'row'} alignContent={'center'} gap={2}>
                  <PaiTextField
                    value={question.externalAssessment.de}
                    placeholder={'Fragetext zur Fremdbewertung...'}
                    label={'Fremdbewertung (deutsch)'}
                    onChange={handleExternalAssessmentQuestionChange(subCategoryIdx, questionIdx, QuestionnaireLanguages.DE)}
                  />
                  <PaiTextField
                    value={question.externalAssessment.en}
                    placeholder={'Fragetext zur Fremdbewertung...'}
                    label={'Fremdbewertung (englisch)'}
                    onChange={handleExternalAssessmentQuestionChange(subCategoryIdx, questionIdx, QuestionnaireLanguages.EN)}
                  />
                </Stack>
                {subCategory.questions.length > 1 ? (
                  <Stack direction={'row'} justifyContent={'flex-end'}>
                    <ButtonBase className={classes.deleteButton} onClick={removeQuestionFromClosedQuestionSubCategory(subCategoryIdx, questionIdx)}>
                      <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={1}>
                        <DisabledByDefaultSharpIcon className={classes.icon} />
                        <Typography className={classes.text}>Frage entfernen</Typography>
                      </Stack>
                    </ButtonBase>
                  </Stack>
                ) : null}
              </Stack>
            ))}
            <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
              <ButtonBase className={classes.addButton} onClick={addNewQuestionToClosedQuestionSubCategory(subCategoryIdx)}>
                <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={1}>
                  <AddBoxSharpIcon className={classes.icon} />
                  <Typography className={classes.text}>weitere Frage</Typography>
                </Stack>
              </ButtonBase>
              {subCategories.length > 1 ? (
                <ButtonBase className={classes.deleteButton} onClick={removeSubCategory(subCategoryIdx)}>
                  <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={1}>
                    <DisabledByDefaultSharpIcon className={classes.icon} />
                    <Typography className={classes.text}>Unterkategorie entfernen</Typography>
                  </Stack>
                </ButtonBase>
              ) : null}
            </Stack>
          </Stack>
        ))}
        <ButtonBase className={classes.addButton} onClick={addNewSubCategory}>
          <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={1}>
            <AddBoxSharpIcon className={classes.icon} />
            <Typography className={classes.text}>weitere Unterkategorie</Typography>
          </Stack>
        </ButtonBase>
      </Stack>
    </BasicActionDialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  container: {
    borderBottom: `1px solid ${theme.palette.greys.light}`,
    paddingBottom: '16px',
  },
  deleteButton: {
    color: theme.palette.error.main,
    '&:hover': {
      color: theme.palette.secondary.main,
    },
  },
  addButton: {
    marginRight: 'auto',
    color: theme.palette.primary.main,
    '&:hover': {
      color: theme.palette.secondary.main,
    },
  },
  icon: {
    fontSize: theme.typography.pxToRem(20),
  },
  text: {
    fontSize: theme.typography.pxToRem(13),
    textDecoration: 'underline',
    textUnderlineOffset: '4px',
  },
}));

export default AddNewClosedQuestionDialog;
